import React, { useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from '../hooks/use-toast';
import { Eye, EyeOff } from 'lucide-react';

const RegisterCandidate = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    profession: '',
    specialisation: '',
    country: '',
    yearsExperience: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast({
        title: 'Error',
        description: 'Passwords do not match',
        variant: 'destructive'
      });
      return;
    }
    
    try {
      const { auth } = await import('../api');
      const response = await auth.registerCandidate(
        { email: formData.email, password: formData.password },
        {
          first_name: formData.firstName,
          last_name: formData.lastName,
          profession: formData.profession,
          specialisation: formData.specialisation,
          country: formData.country,
          years_experience: parseInt(formData.yearsExperience)
        }
      );
      
      // Store token and user info
      localStorage.setItem('authToken', response.access_token);
      localStorage.setItem('userType', response.user_type);
      localStorage.setItem('userName', response.user_name);
      
      toast({
        title: 'Registration Successful',
        description: 'Welcome to Luma Med Global! Redirecting to your dashboard...',
      });
      
      setTimeout(() => {
        navigate('/candidate/dashboard');
      }, 1500);
    } catch (error) {
      toast({
        title: 'Registration Failed',
        description: error.response?.data?.detail || 'An error occurred during registration',
        variant: 'destructive'
      });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <div className="pt-24 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto">
          <Card className="border-2 border-gray-200">
            <CardHeader className="text-center pb-4">
              <CardTitle className="text-3xl font-bold text-gray-900">Create Candidate Account</CardTitle>
              <p className="text-gray-600 mt-2">Join thousands of medical professionals finding opportunities in Saudi Arabia</p>
            </CardHeader>
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="firstName">First Name</Label>
                    <Input
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      placeholder="John"
                      required
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label htmlFor="lastName">Last Name</Label>
                    <Input
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      placeholder="Smith"
                      required
                      className="mt-2"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="john@example.com"
                    required
                    className="mt-2"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="profession">Profession</Label>
                    <Select onValueChange={(value) => setFormData({...formData, profession: value})} required>
                      <SelectTrigger className="mt-2">
                        <SelectValue placeholder="Select profession" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="nurse">Registered Nurse</SelectItem>
                        <SelectItem value="doctor">Medical Doctor</SelectItem>
                        <SelectItem value="specialist">Specialist</SelectItem>
                        <SelectItem value="surgeon">Surgeon</SelectItem>
                        <SelectItem value="allied">Allied Health Professional</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="specialisation">Specialisation</Label>
                    <Input
                      id="specialisation"
                      name="specialisation"
                      value={formData.specialisation}
                      onChange={handleChange}
                      placeholder="e.g., Critical Care"
                      required
                      className="mt-2"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="country">Current Country</Label>
                    <Select onValueChange={(value) => setFormData({...formData, country: value})} required>
                      <SelectTrigger className="mt-2">
                        <SelectValue placeholder="Select country" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="uk">United Kingdom</SelectItem>
                        <SelectItem value="us">United States</SelectItem>
                        <SelectItem value="canada">Canada</SelectItem>
                        <SelectItem value="australia">Australia</SelectItem>
                        <SelectItem value="ireland">Ireland</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="yearsExperience">Years of Experience</Label>
                    <Input
                      id="yearsExperience"
                      name="yearsExperience"
                      type="number"
                      value={formData.yearsExperience}
                      onChange={handleChange}
                      placeholder="e.g., 5"
                      required
                      className="mt-2"
                      min="0"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="password">Password</Label>
                  <div className="relative mt-2">
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="Create a strong password"
                      required
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>

                <div>
                  <Label htmlFor="confirmPassword">Confirm Password</Label>
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    placeholder="Confirm your password"
                    required
                    className="mt-2"
                  />
                </div>

                <Button type="submit" className="w-full bg-[#42b1c2] hover:bg-[#3a9fb0] text-white py-6">
                  Create Account
                </Button>

                <div className="text-center text-sm text-gray-600">
                  Already have an account?{' '}
                  <Link to="/login" className="text-[#42b1c2] hover:text-[#3a9fb0] font-medium">
                    Sign in
                  </Link>
                </div>

                <p className="text-xs text-center text-gray-500">
                  By creating an account, you agree to our Terms of Service and Privacy Policy
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default RegisterCandidate;